import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Plus, Search, Store, MoreVertical, Pencil, Power, MapPin, Phone } from "lucide-react";
import { listBranches, deleteBranch } from "../../api/branches";
import PageHeader from "../../components/PageHeader";
import { Card, Button, Input, EmptyState, TableSkeleton, ConfirmDialog, Avatar } from "../../components/ui";
import { StatusChip } from "../../components/Chips";
import Pagination from "../../components/Pagination";
import { formatDate } from "../../lib/records";
import BranchFormModal from "./BranchFormModal";

export default function BranchesList() {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null); // { top, left } in viewport coords
  const [formOpen, setFormOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listBranches({
        page,
        limit: 10,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      });
      setItems(res.data.items);
      setPagination(res.data.pagination);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on scroll/resize so a stale-positioned menu never gets left behind.
  useEffect(() => {
    if (!openMenuId) return;
    function close() {
      setOpenMenuId(null);
    }
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [openMenuId]);

  function toggleMenu(branchId, e) {
    if (openMenuId === branchId) {
      setOpenMenuId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 140;
    setMenuAnchor({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - menuWidth),
    });
    setOpenMenuId(branchId);
  }

  function openCreate() {
    setEditingBranch(null);
    setFormOpen(true);
  }
  function openEdit(branch) {
    setEditingBranch(branch);
    setFormOpen(true);
    setOpenMenuId(null);
  }
  function onSaved() {
    setFormOpen(false);
    fetchData();
  }

  async function confirmDelete() {
    setDeleting(true);
    try {
      await deleteBranch(deleteTarget._id);
      setDeleteTarget(null);
      fetchData();
    } finally {
      setDeleting(false);
    }
  }

  const activeMenuBranch = items.find((b) => b._id === openMenuId) || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Branches"
          subtitle="Onboard and manage every outlet under your chain."
          actions={
            <Button
              onClick={openCreate}
              className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 text-sm sm:text-base px-3 sm:px-4 py-1.5 sm:py-2"
            >
              <Plus size={16} className="hidden sm:inline mr-1" /> Onboard branch
            </Button>
          }
        />

        <Card className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-emerald-100/60 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {["", "ACTIVE", "INACTIVE", "SUSPENDED"].map((s) => (
                  <button
                    key={s || "all"}
                    onClick={() => setStatus(s)}
                    className={`whitespace-nowrap rounded-full px-3 sm:px-3.5 py-1 sm:py-1.5 text-xs sm:text-body-sm font-medium transition-colors duration-200 ${
                      status === s
                        ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md shadow-emerald-500/20"
                        : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
                    }`}
                  >
                    {s === "" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <TableSkeleton rows={6} cols={5} />
          ) : items.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={Store}
                title="No branches found"
                description={search || status ? "Try a different search or filter." : "Onboard your first branch to get started."}
                action={
                  !search && !status && (
                    <Button
                      onClick={openCreate}
                      className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25"
                    >
                      <Plus size={16} /> Onboard branch
                    </Button>
                  )
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="text-xs sm:text-label-sm uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Branch</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Phone</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden sm:table-cell">Location</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Status</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden lg:table-cell">Last login</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-right w-12 sm:w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {items.map((b) => (
                    <tr key={b._id} className="group text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-50/50 transition-colors duration-200">
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <Link to={`/branches/${b._id}`} className="flex items-center gap-2 sm:gap-3">
                          <Avatar name={b.name} size={28} className="bg-gradient-to-br from-emerald-400 to-green-500 text-white text-xs" />
                          <div>
                            <p className="font-medium text-gray-800 text-xs sm:text-sm">{b.name}</p>
                            <p className="text-xs text-gray-500">{b.partnerId}</p>
                          </div>
                        </Link>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600">
                        <span className="flex items-center gap-1">
                          <Phone size={11} className="text-gray-400" /> +91 {b.phone}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600 hidden sm:table-cell">
                        {b.location?.city ? (
                          <span className="flex items-center gap-1">
                            <MapPin size={11} className="text-gray-400" />
                            {b.location.city}
                            {b.location.state ? `, ${b.location.state}` : ""}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <StatusChip status={b.status} />
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-500 hidden lg:table-cell">
                        {b.lastLoginAt ? formatDate(b.lastLoginAt) : "Never"}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-right relative">
                        <button
                          onClick={(e) => toggleMenu(b._id, e)}
                          className="rounded-md p-1.5 text-gray-400 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="px-3 sm:px-4 border-t border-emerald-100/60 pt-3 sm:pt-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>

        {/* Dropdown menu rendered via portal so it can never be clipped by
            the table's overflow-x-auto or the Card's overflow-hidden. */}
        {openMenuId && activeMenuBranch && menuAnchor &&
          createPortal(
            <div
              ref={menuRef}
              style={{ position: "fixed", top: menuAnchor.top, left: menuAnchor.left, minWidth: 140 }}
              className="z-[9999] rounded-md border border-emerald-100 bg-white py-1 shadow-xl shadow-emerald-200/50"
            >
              <button
                onClick={() => openEdit(activeMenuBranch)}
                className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-xs sm:text-body-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
              >
                <Pencil size={14} /> Edit
              </button>
              <button
                onClick={() => {
                  setDeleteTarget(activeMenuBranch);
                  setOpenMenuId(null);
                }}
                className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-xs sm:text-body-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
              >
                <Power size={14} /> Delete
              </button>
            </div>,
            document.body
          )}

        <BranchFormModal open={formOpen} onClose={() => setFormOpen(false)} branch={editingBranch} onSaved={onSaved} />

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          loading={deleting}
          danger
          title="Delete branch?"
          description={`This will remove ${deleteTarget?.name ?? "this branch"} from active lists. Its staff and complaints stay intact.`}
          confirmLabel="Delete"
        />
      </div>
    </div>
  );
}